import '../../common/bn-modal-button';
import '../../common/bn-property';
import '../../common/bn-surface';
import '../../common/bn-icon';
import '../../notes/site-notes';
import '../../organizations/parts/farm-header';
import '../../organizations/parts/organization-ancestors-bread-crumbs';
import '../../organizations/props/organization-name';
import '../../work-orders/parts/work-order-list';
import '../actions/location-delegate-modal';
import '../actions/location-assign-button';
import '../actions/location-unassign-button';
import '../actions/location-undelegate';
import './work-orders-by-state';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class WorkOrderLocation extends LitEntity {
  constructor() {
    super();
    this.expandWorkOrders = true;
  }

  static get properties() {
    return {
      hasWorkOrdersOnHold: { type: Boolean, attribute: false },
      expandWorkOrders: { type: Boolean, attribute: 'expand-work-orders' },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      .header {
        display: flex;
        margin-bottom: .5rem;
      }
      farm-header {
        flex-grow: 1;
      }
      bn-property {
        margin: .5rem 0;
        white-space: normal;
      }
      bn-property[label=State] {
        font-weight: bold;
      }
      .properties {
        display: flex;
      }
      .properties bn-property {
        margin: 0 2rem .5rem 0;
        white-space: unset;
      }
      .actions {
        display: flex;
        align-items: flex-start;
        justify-content: flex-end;
      }
      .actions > * {
        white-space: nowrap;
        margin: .5em 0 0 .5em;
      }
      .no-data {
        text-align: center;
        padding: 1em;
      }
      .actions bn-icon {
        margin-left: .5rem;
      }
      .well h5 {
        margin: 1em .5em .5em;
        color: var(--gray-80);
      }
      @media(max-width: 1000px) {
        .header {
          flex-direction: column;
        }
        .actions {
          flex-wrap: wrap;
        }
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html``;
    }
    const farmLink = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/farm');
    const servicePartnerLink = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/service-partner');
    const installerLink = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/installer');
    return html`
      <bn-surface class="location">
        <div class="body">
          <div class="header">
            <farm-header .href="${farmLink.href}" include-map-links></farm-header>
            <div class="actions">${this._renderActions()}</div>
          </div>
          ${renderIf(servicePartnerLink, () => html`
            <bn-property stacked label="Service Partner">
              <organization-ancestors-bread-crumbs .href="${servicePartnerLink.href}"></organization-ancestors-bread-crumbs>
            </bn-property>
          `)}
          ${renderIf(installerLink, () => html`
            <div class="properties">
              <bn-property stacked label="Scheduled For">
                ${this.entity.properties.scheduledFor}
              </bn-property>
              <bn-property stacked label="Installer">
                <a href="/scheduler/user?userId=${encodeURIComponent(installerLink.href)}">
                  <organization-name .href="${installerLink.href}"></organization-name>
                </a>
              </bn-property>
            </div>
          `)}
        </div>

        <site-notes .href="${farmLink.href}"></site-notes>

        ${this._renderWorkOrders()}
      </bn-surface>
    `;
  }

  _renderWorkOrders() {
    if (!this.expandWorkOrders) {
      return html``;
    }
    const workOrdersLink = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/work-orders');
    return html`
      <div class="well">
        <h5>Ready</h5>
        <work-order-list .href="${workOrdersLink.href}" hide-state hide-address></work-order-list>
        <div ?hidden="${!this.hasWorkOrdersOnHold}">
          <h5>On Hold</h5>
          <work-orders-by-state
            .href="${workOrdersLink.href}"
            state="hold"
            hide-state
            hide-address
            @innerupdate="${this._onUpdateWorkOrderOnHold}"
          ></work-orders-by-state>
        </div>
      </div>
    `;
  }

  _renderActions() {
    const actions = [];
    const locationHref = this.getHref('self');
    if (this.entity.hasAction('delegate-partner')) {
      actions.push(html`
        <bn-modal-button button-class="button--contained">
          <location-delegate-modal
            slot="modal"
            .href="${locationHref}"
            @response="${this._reload}"
          ></location-delegate-modal>
          <span slot="text">Delegate</span>
        </bn-modal-button>
      `);
    }
    if (this.entity.hasAction('undelegate-partner')) {
      actions.push(html`
        <location-undelegate-button
          .href="${locationHref}"
          @response="${this._reload}"
        ></location-undelegate-button>
      `);
    }
    if (this.entity.hasAction('select-installer')) {
      actions.push(html`
        <location-assign-button
          .href="${locationHref}"
          @response="${this._reload}"
        ></location-assign-button>
      `);
    }
    if (this.entity.hasAction('unassign-installer')) {
      actions.push(html`
        <location-unassign-button
          .href="${locationHref}"
          @response="${this._reload}"
        ></location-unassign-button>
      `);
    }

    const expandIcon = this.expandWorkOrders ? 'fa-chevron-up' : 'fa-chevron-down';
    actions.push(html`
      <a class="button button-contained" @click="${() => this.expandWorkOrders = !this.expandWorkOrders}">
        Work Orders <bn-icon name="fa ${expandIcon}"></bn-icon>
      </a>
    `);
    return actions;
  }

  _onUpdateWorkOrderOnHold(e) {
    const { entity } = e.detail;
    this.hasWorkOrdersOnHold = entity && entity.hasSubEntityByClass('work-order');
  }

  _reload() {
    entityStore.invalidateHref(this.href);
    this.dispatchEvent(new CustomEvent('reload'));
  }
}

customElements.define('work-order-location', WorkOrderLocation);
